<template>
    <section id="residence" class="residence-card card mb-3">
        <form
            ref="residenceForm"
            v-if="residence"
            class="card-body"
            v-click-outside="onClickOutside"
            @click="focusBlock"
        >
            <div class="card-heading">
                <h5 class="card-name">
                    {{ $t('lib_address') }}
                </h5>
            </div>
            <hr />
            <div class="row">
                <div
                    class="form-group col-sm-8 col-lg-4"
                    :class="{ 'required-group': isFieldRequired('adStreet') }"
                >
                    <label for="adStreet" class="form-label">
                        {{ $t('lib_street') }}
                    </label>
                    <input
                        id="adStreet"
                        type="text"
                        v-model="residence.adStreet"
                        class="form-control"
                        :class="{
                            'is-invalid': errorMessage('adStreet', block.name),
                        }"
                    />
                    <div
                        v-if="errorMessage('adStreet', block.name)"
                        class="invalid-feedback"
                    >
                        {{ $t(errorMessage('adStreet', block.name)) }}
                    </div>
                </div>
                <div
                    class="form-group col-sm-4 col-lg-2"
                    :class="{
                        'required-group': isFieldRequired('adPostalCode'),
                    }"
                >
                    <label for="postal_code" class="form-label">
                        {{ $t('lib_postal_code') }}
                    </label>
                    <input
                        id="postal_code"
                        type="text"
                        v-model="residence.adPostalCode"
                        class="form-control"
                        :class="{
                            'is-invalid': errorMessage(
                                'adPostalCode',
                                block.name
                            ),
                        }"
                    />
                    <div
                        v-if="errorMessage('adPostalCode', block.name)"
                        class="invalid-feedback"
                    >
                        {{ $t(errorMessage('adPostalCode', block.name)) }}
                    </div>
                </div>
                <div
                    class="form-group col-sm-6 col-lg-3"
                    :class="{ 'required-group': isFieldRequired('adCity') }"
                >
                    <label for="place" class="form-label">
                        {{ $t('col_city') }}
                    </label>
                    <input
                        id="place"
                        type="text"
                        v-model="residence.adCity"
                        class="form-control"
                        :class="{
                            'is-invalid': errorMessage('adCity', block.name),
                        }"
                    />
                    <div
                        v-if="errorMessage('adCity', block.name)"
                        class="invalid-feedback"
                    >
                        {{ $t(errorMessage('adCity', block.name)) }}
                    </div>
                </div>
                <div
                    class="form-group col-sm-6 col-lg-3"
                    :class="{
                        'required-group': isFieldRequired('adIsoCountryCode'),
                    }"
                >
                    <label for="country" class="form-label">
                        {{ $t('lib_country') }}
                    </label>
                    <Dropdown
                        inputId="country"
                        appendTo="self"
                        v-model="residence.adIsoCountryCode"
                        :class="{
                            'is-invalid': errorMessage(
                                'adIsoCountryCode',
                                block.name
                            ),
                        }"
                        :filter="true"
                        class="w-100"
                        :showClear="true"
                        :options="groupedCountries"
                        optionLabel="name"
                        optionValue="code"
                        optionGroupLabel="group"
                        optionGroupChildren="countries"
                        :placeholder="$t('cand_choose')"
                    />
                    <div
                        v-if="errorMessage('adIsoCountryCode', block.name)"
                        class="invalid-feedback"
                    >
                        {{ $t(errorMessage('adIsoCountryCode', block.name)) }}
                    </div>
                </div>
            </div>
            <div class="card-footer d-flex justify-content-end mt-3" v-if="formWasChanged">
                <div class="card-actions">
                    <button
                        type="button"
                        class="card-action save-action btn btn-text me-2"
                        @click="validate()"
                        :disabled="appLoading"
                    >
                        <font-awesome-icon icon="far fa-save"  class="fs-5 btn-icon"/>
                        <span class="btn-label">
                            {{ $t('btn_save') }}
                        </span>
                    </button>
                    <button
                        type="button"
                        class="card-action undo-changes-action btn btn-text"
                        @click="undoChanges()"
                        :disabled="appLoading"
                    >
                        <font-awesome-icon icon="fas fa-undo"  class="btn-icon"/>
                        <span class="btn-label">
                            {{ $t('btn_cancel') }}
                        </span>
                    </button>
                </div>
            </div>
        </form>
    </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Dropdown from 'primevue/dropdown';
import _ from 'lodash';

export default {
    name: 'Residence',
    components: {
        Dropdown,
    },
    props: {
        block: {
            type: Object,
            required: true,
        },
        applicationId: {
            type: String,
            required: true,
        },
        applicationType: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            residence: null,
            listenToClickOutside: false,
            formWasChanged: false,
        };
    },
    computed: {
        ...mapGetters([
            'countries',
            'errorMessage',
            'isFieldRequired',
            'residenceData',
            'appLoading',
        ]),
        groupedCountries() {
            return (
                _.chain(this.countries)
                    // Group the elements of Array based on `eu/non-eu`
                    .groupBy('group')
                    // `key` is group's name (group), `value` is the array of countries
                    .map((value, key) => ({ group: key, countries: value }))
                    .value()
            );
        },
        // formWasChanged() {
        //     if(JSON.stringify(this.residence) === JSON.stringify(this.residenceData)) {
        //         // this.clearBlockErrors(this.block.name);
        //     }
        //     return (
        //         JSON.stringify(this.residence) !==
        //         JSON.stringify(this.residenceData)
        //     );
        // },
    },
    methods: {
        ...mapActions([
            'validateBlock',
            'updateBlock',
            'clearBlockErrors',
            'focusOnBlock',
        ]),
        validate() {
            this.clearBlockErrors(this.block.name);
            this.validateBlock({
                blockData: { ...this.residence },
                blockName: this.block.name,
            }).then(
                () => {
                    // success
                    this.updateBlock({
                        blockName: this.block.name,
                        application: {
                            applicationId: this.applicationId,
                            applicationType: this.applicationType,
                            applicationBlocks: {
                                [this.block.name]: { ...this.residence },
                            },
                        },
                    }).then(
                        () => {
                            // success
                            this.listenToClickOutside = false;
                            this.$toast.add({
                                severity: 'success',
                                detail: this.$t('mess_save_success'),
                                life: 3000,
                            });
                            this.loadData();
                            this.$emit('checkErrorForOtherBlocks');
                        },
                        () => {
                            // error
                            this.listenToClickOutside = false;
                            this.$toast.add({
                                severity: 'error',
                                detail: this.$t('mess_save_failed'),
                                life: 3000,
                            });
                        }
                    );
                },
                () => {
                    // error
                    this.listenToClickOutside = false;
                    this.$toast.add({
                        severity: 'error',
                        detail: this.$t('mess_save_failed'),
                        life: 3000,
                    });
                }
            );
        },
        onClickOutside() {
            if (
                this.listenToClickOutside &&
                this.formWasChanged &&
                !this.appLoading
            ) {
                this.clearBlockErrors(this.block.name);
                this.validate();
            }
            this.focusOnBlock(null);
        },
        undoChanges() {
            this.loadData();
            this.clearBlockErrors(this.block.name);
        },
        loadData() {
            this.residence = _.cloneDeep(this.residenceData);
        },
        focusBlock() {
            this.listenToClickOutside = true;
            this.focusOnBlock({
                name: 'residence',
                label: this.$t('lib_address'),
            });
        },
    },
    mounted() {
        this.loadData();
    },
    watch: {
        // whenever residence object changes, this function will run and this way we will now if the form data was changed so we can save changes;
        residence: {
            handler(newData, oldData) {
                const storeData = {
                    ...this.residenceData,
                };
                this.formWasChanged =
                    JSON.stringify(newData) !== JSON.stringify(storeData);

                if (!this.formWasChanged && oldData) {
                    // checking for oldData to prevent clearing the errors on firstload;
                    this.clearBlockErrors(this.block.name);
                }
            },
            deep: true,
        },
    },
};
</script>
